<template>
  <a-modal
    v-model:open="visible"
    title="任务选择"
    width="800px"
    @ok="handleConfirm"
    @cancel="handleCancel"
  >
    <div class="task-selection-modal">
      <!-- 步骤指示器 -->
      <a-steps :current="currentStep" size="small" class="steps">
        <a-step title="选择执行方式" />
        <a-step title="配置任务信息" />
        <a-step title="确认提交" />
      </a-steps>

      <!-- 步骤1: 选择执行方式 -->
      <div v-if="currentStep === 0" class="step-content">
        <div class="project-summary">
          <h4>项目信息</h4>
          <div class="summary-card">
            <div class="summary-item">
              <span class="label">项目类型:</span>
              <a-tag :color="getProjectTypeColor(projectType)">
                {{ getProjectTypeText(projectType) }}项目
              </a-tag>
            </div>
            <div class="summary-item" v-if="projectTitle">
              <span class="label">项目名称:</span>
              <span class="value">{{ projectTitle }}</span>
            </div>
            <div class="summary-item" v-if="sourceLanguage">
              <span class="label">源语言:</span>
              <span class="value">{{ sourceLanguage }}</span>
            </div>
            <div class="summary-item" v-if="resolvedTargetLanguage">
              <span class="label">目标语言:</span>
              <a-tag color="blue" size="small">
                {{ getLanguageName(resolvedTargetLanguage) }}
              </a-tag>
            </div>
          </div>
        </div>

        <div class="execution-options">
          <h4>选择任务执行方式</h4>
          <a-radio-group v-model:value="selectedTaskType" class="option-group">
            <div class="option-card" :class="{ active: selectedTaskType === 'self' }">
              <a-radio value="self">
                <div class="option-content">
                  <div class="option-header">
                    <UserOutlined class="option-icon" />
                    <span class="option-title">自己完成</span>
                  </div>
                  <div class="option-desc">由您自己完成此翻译任务，无需指派给其他人</div>
                  <div class="option-features">
                    <a-tag color="blue" size="small">立即开始</a-tag>
                    <a-tag color="green" size="small">完全控制</a-tag>
                  </div>
                </div>
              </a-radio>
            </div>

            <div class="option-card" :class="{ active: selectedTaskType === 'group' }">
              <a-radio value="group">
                <div class="option-content">
                  <div class="option-header">
                    <TeamOutlined class="option-icon" />
                    <span class="option-title">同组指派</span>
                  </div>
                  <div class="option-desc">指派给同组内的其他成员完成，适合团队协作</div>
                  <div class="option-features">
                    <a-tag color="blue" size="small">团队协作</a-tag>
                    <a-tag color="green" size="small">内部指派</a-tag>
                  </div>
                </div>
              </a-radio>
            </div>

            <div class="option-card" :class="{ active: selectedTaskType === 'metalingual' }">
              <a-radio value="metalingual">
                <div class="option-content">
                  <div class="option-header">
                    <GlobalOutlined class="option-icon" />
                    <span class="option-title">METALINGUAL指派</span>
                  </div>
                  <div class="option-desc">指派给METALINGUAL专业组成员完成，专业翻译服务</div>
                  <div class="option-features">
                    <a-tag color="blue" size="small">专业服务</a-tag>
                    <a-tag color="purple" size="small">专业译员</a-tag>
                  </div>
                </div>
              </a-radio>
            </div>
          </a-radio-group>
        </div>
      </div>

      <!-- 步骤2: 配置任务信息 -->
      <div v-if="currentStep === 1" class="step-content">
        <h3>配置任务信息</h3>
        
        <!-- 被指派人选择 -->
        <div v-if="showUserSelection" class="user-selection">
          <h4>选择被指派人</h4>
          <a-select
            v-model:value="selectedUserId"
            placeholder="请选择被指派人"
            style="width: 100%"
            :loading="loadingUsers"
            show-search
            size="large"
            @change="onUserChange"
          >
            <a-select-option
              v-for="user in availableUsers"
              :key="user.id"
              :value="user.id"
            >
              <div class="user-option">
                <a-avatar size="small" :src="user.avatar || undefined">
                  {{ user.name?.charAt(0) }}
                </a-avatar>
                <div class="user-info">
                  <span class="user-display-name">{{ formatUserDisplayName(user) }}</span>
                </div>
              </div>
            </a-select-option>
          </a-select>
        </div>

        <!-- 任务描述 -->
        <div v-if="selectedTaskType !== 'self'" class="task-description">
          <h4>任务描述</h4>
          <a-textarea
            v-model:value="taskDescription"
            placeholder="请输入任务描述（可选）"
            :rows="3"
            :maxlength="500"
            show-count
          />
        </div>


      </div>

      <!-- 步骤3: 确认提交 -->
      <div v-if="currentStep === 2" class="step-content">
        <h3>任务信息确认</h3>
        
        <div class="confirmation-summary">
          <div class="summary-card">
            <div class="summary-section">
              <h5>执行方式</h5>
              <div class="summary-item">
                <span class="label">任务类型:</span>
                <span class="value">{{ getTaskTypeText(selectedTaskType) }}</span>
              </div>
              <div v-if="selectedUserId" class="summary-item">
                <span class="label">被指派人:</span>
                <span class="value">{{ getSelectedUserName() }}</span>
              </div>
            </div>

            <div class="summary-section">
              <h5>任务配置</h5>
              <div class="summary-item" v-if="resolvedTargetLanguage">
                <span class="label">目标语言:</span>
                <a-tag color="blue">
                  {{ getLanguageName(resolvedTargetLanguage) }}
                </a-tag>
              </div>
              <div v-if="taskDescription && selectedTaskType !== 'self'" class="summary-item">
                <span class="label">描述:</span>
                <span class="value">{{ taskDescription }}</span>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 自定义底部按钮 -->
    <template #footer>
      <div class="modal-footer">
        <a-button @click="handleCancel">取消</a-button>
        <a-button 
          v-if="currentStep > 0" 
          @click="handlePrevious"
        >
          上一步
        </a-button>
        <a-button 
          v-if="currentStep < 2" 
          type="primary" 
          @click="handleNext"
          :disabled="!canGoNext"
        >
          下一步
        </a-button>
        <a-button 
          v-if="currentStep === 2" 
          type="primary" 
          @click="handleConfirm"
          :disabled="!canConfirm"
        >
          确认提交
        </a-button>
      </div>
    </template>
  </a-modal>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { message } from 'ant-design-vue'
import { useRouter } from 'vue-router'
import {
  UserOutlined,
  TeamOutlined,
  GlobalOutlined
} from '@ant-design/icons-vue'
import {
  getTaskSelectionOptions,
  assignVideoTask,
  assignDocumentTask,
  acceptTask,
  type TaskSelectionOptions,
  type TaskAssignmentCreate,
} from '@/modules/task/api'
import { getWorkItem } from '@/modules/project/api/work-item'
import { type UserSummary } from '@/modules/user/api'
import { ProjectType } from '@/modules/project/api'
import { LanguageAPI, type Language, LanguageType } from '@/modules/system/api/language'
import { runApiBatch, type BatchContext } from '@/shared/api/use-api-batch'

interface Props {
  /** 条目 ID（推荐） */
  workItemId?: number
  /** @deprecated 语义为 work_item_id，请改用 workItemId */
  projectId?: number
  projectType?: string
  projectTitle?: string
  sourceLanguage?: string
  targetLanguages?: string[]
}

const props = defineProps<Props>()
const resolvedWorkItemId = computed(() => props.workItemId ?? props.projectId)

const emit = defineEmits<{
  taskAssigned: [taskId: number]
  cancel: []
}>()

const router = useRouter()
const visible = defineModel<boolean>('visible', { default: false })

// 状态管理
const loading = ref(false)
const loadingUsers = ref(false)
const loadingLanguages = ref(false)
const currentStep = ref(0)
const selectedTaskType = ref('self')
const selectedUserId = ref<number>()
/** 目的语只读：来自上传建条目时写入的 WorkItem.target_language（方案 B） */
const resolvedTargetLanguage = ref<string>('')
const availableLanguages = ref<Language[]>([])
const taskDescription = ref('')

const taskOptions = ref<TaskSelectionOptions>()

const handleConfirm = async () => {
  if (!resolvedWorkItemId.value || !props.projectType) {
    message.error('项目信息不完整')
    return
  }

  if (!resolvedTargetLanguage.value) {
    message.error('条目未配置目标语言，请返回上传步骤设置')
    return
  }

  try {
    loading.value = true
    const workItem = await getWorkItem(resolvedWorkItemId.value)
    const targetLanguage = workItem.target_language || resolvedTargetLanguage.value
    if (!targetLanguage) {
      message.error('条目未配置目标语言，请返回上传步骤设置')
      return
    }
    resolvedTargetLanguage.value = targetLanguage

    const taskId = workItem.current_task_id
    if (!taskId) {
      message.error('当前没有可指派的任务')
      return
    }

    let task
    if (selectedTaskType.value === 'self') {
      task = await acceptTask(taskId)
    } else {
      if (!selectedUserId.value) {
        message.error('请选择被指派人')
        return
      }
      const assignmentData: TaskAssignmentCreate = {
        assignee_id: selectedUserId.value,
      }
      task = props.projectType === 'video'
        ? await assignVideoTask(resolvedWorkItemId.value, assignmentData)
        : await assignDocumentTask(resolvedWorkItemId.value, assignmentData)
    }

    message.success('任务创建成功')
    
    // 根据任务类型决定跳转位置和处理逻辑
    if (selectedTaskType.value === 'self') {
      // 自己完成的任务：先关闭模态框，触发事件，然后跳转到翻译页面
      visible.value = false
      emit('taskAssigned', task.id)
      
      // 跳转到翻译页面，可以立即开始翻译
      if (props.projectType === 'video') {
        router.push({
          name: 'TranslationReview',
          params: { id: resolvedWorkItemId.value?.toString() }
        })
      } else if (props.projectType === 'document') {
        router.push({
          name: 'DocumentTranslation',
          params: { id: resolvedWorkItemId.value?.toString() },
          query: { target_language: targetLanguage }
        })
      }
    } else {
      // 指派任务（同组/METALINGUAL）：立即跳转到项目列表页面，不触发 taskAssigned 事件
      // 先执行跳转，避免页面状态被更新导致跳转失败
      // 使用 replace 而不是 push，避免可以返回导致状态不一致
      router.replace({
        path: '/project/my-projects',
      })
      
      // 关闭模态框
      visible.value = false
      
      // 发起人回项目管理；被指派人应在「收到的任务」接受并进入校验（勿依赖项目可读）
      message.info({
        content: '任务已指派成功！被指派人请在「收到的任务」中接受并进入校验；您可在项目管理中查看状态。',
        duration: 5
      })
      
      // 任务通知已由后端自动发送，前端不再发送
    }

  } catch (error: any) {
    console.error('任务创建失败:', error)
  } finally {
    loading.value = false
  }
}

const handleCancel = () => {
  console.log('取消')
  visible.value = false
  emit('cancel')
}

const handleNext = () => {
  // 自己完成时无需配置被指派人；目的语已在上传时确定，跳过空步骤
  if (currentStep.value === 0 && selectedTaskType.value === 'self') {
    currentStep.value = 2
    return
  }
  if (currentStep.value < 2) {
    currentStep.value++
  }
}

const handlePrevious = () => {
  if (currentStep.value === 2 && selectedTaskType.value === 'self') {
    currentStep.value = 0
    return
  }
  if (currentStep.value > 0) {
    currentStep.value--
  }
}

const getTaskTypeText = (taskType: string) => {
  const texts: Record<string, string> = {
    'self': '自己完成',
    'group': '同组指派',
    'metalingual': 'METALINGUAL指派'
  }
  return texts[taskType] || '自己完成'
}

const getLanguageName = (langCode: string) => {
  const lang = availableLanguages.value.find(l => l.language_code === langCode)
  return lang ? lang.chinese_name : langCode
}

const getProjectTypeColor = (projectType?: string) => {
  return projectType === 'video' ? 'blue' : 'green'
}

const getProjectTypeText = (projectType?: string) => {
  return projectType === 'video' ? '视频' : '文档'
}



// 计算属性
const showUserSelection = computed(() => {
  return selectedTaskType.value !== 'self'
})

const availableUsers = computed(() => {
  if (selectedTaskType.value === 'group') {
    return taskOptions.value?.same_group_users || []
  } else if (selectedTaskType.value === 'metalingual') {
    return taskOptions.value?.metalingual_users || []
  }
  return []
})

const canGoNext = computed(() => {
  if (currentStep.value === 0) {
    return !!selectedTaskType.value
  } else if (currentStep.value === 1) {
    // 目的语在上传建条目时已定，此处不再二次选择
    return selectedTaskType.value === 'self' || !!selectedUserId.value
  }
  return true
})

const canConfirm = computed(() => {
  return canGoNext.value && currentStep.value === 2 && !!resolvedTargetLanguage.value
})

// 监听弹窗显示状态
watch(visible, async (isVisible) => {
  if (isVisible && resolvedWorkItemId.value && props.projectType) {
    currentStep.value = 0
    await runApiBatch([
      (ctx) => loadTaskOptions(ctx),
      (ctx) => loadLanguages(ctx),
      () => resolveTargetLanguageFromWorkItem(),
    ])
  } else if (!isVisible) {
    resetForm()
  }
})

const loadTaskOptions = async (batchCtx?: BatchContext) => {
  if (!resolvedWorkItemId.value || !props.projectType) return

  const manageLoading = !batchCtx
  try {
    if (manageLoading) {
      loadingUsers.value = true
    }

    const options = await getTaskSelectionOptions(
      resolvedWorkItemId.value,
      props.projectType as ProjectType,
      batchCtx?.requestConfig,
    )

    taskOptions.value = options
  } catch (error) {
    if (!batchCtx) {
      console.error('加载任务选择选项失败:', error)
    }
    throw error
  } finally {
    if (manageLoading) {
      loadingUsers.value = false
    }
  }
}

const loadLanguages = async (batchCtx?: BatchContext) => {
  const manageLoading = !batchCtx
  try {
    if (manageLoading) {
      loadingLanguages.value = true
    }

    const languageOptions = await LanguageAPI.getLanguageOptions({
      language_type: LanguageType.TARGET,
      status: true
    }, batchCtx?.requestConfig)
    
    // 转换为Language格式以保持兼容性（仅用于只读展示语言名称）
    availableLanguages.value = languageOptions.map((option) => ({
      id: 0, // 临时ID，实际不使用
      chinese_name: option.label.split(' (')[0],
      english_name: option.label.split(' (')[1]?.replace(')', '') || '',
      language_code: option.value,
      microsoft_code: option.microsoft_code,
      language_type: option.language_type,
      status: true,
      created_at: '',
      updated_at: ''
    }))
    
  } catch (error) {
    if (!batchCtx) {
      console.error('加载语言列表失败:', error)
    }
    throw error
  } finally {
    if (manageLoading) {
      loadingLanguages.value = false
    }
  }
}

/** 目的语权威来源：WorkItem.target_language；props 仅作预填 */
const resolveTargetLanguageFromWorkItem = async () => {
  resolvedTargetLanguage.value = props.targetLanguages?.[0] ?? ''
  if (!resolvedWorkItemId.value) return
  try {
    const workItem = await getWorkItem(resolvedWorkItemId.value)
    if (workItem.target_language) {
      resolvedTargetLanguage.value = workItem.target_language
    }
  } catch (error) {
    console.error('解析条目目标语言失败:', error)
  }
}

// 重置表单
const resetForm = () => {
  currentStep.value = 0
  selectedTaskType.value = 'self'
  selectedUserId.value = undefined
  taskDescription.value = ''
  resolvedTargetLanguage.value = props.targetLanguages?.[0] ?? ''
}

// 格式化用户显示名称
const formatUserDisplayName = (user: UserSummary) => {
  if (!user) return ''
  
  let displayName = user.name || ''
  
  // 添加备注名（如果存在）
  if (user.name_remark) {
    displayName += `(${user.name_remark})`
  }
  
  // 添加语言（如果存在）
  if (user.language) {
    displayName += `-${user.language}`
  }
  
  return displayName
}

// 获取选中用户名称
const getSelectedUserName = () => {
  const user = availableUsers.value.find(u => u.id === selectedUserId.value)
  if (!user) return ''
  
  return formatUserDisplayName(user)
}

// 处理任务类型变更
watch(selectedTaskType, () => {
  selectedUserId.value = undefined
})

// 被指派人选择变化处理
const onUserChange = (userId: number) => {
  console.log('选择的被指派人ID:', userId)
}
</script>

<style scoped>
.task-selection-modal {
  padding: 16px;
}

.steps {
  margin-bottom: 24px;
}

.step-content {
  min-height: 200px;
}

.execution-options {
  margin-top: 16px;
}

.option-group {
  width: 100%;
}

.option-card {
  width: 100%;
  padding: 16px;
  border: 2px solid #f0f0f0;
  border-radius: 8px;
  margin-bottom: 12px;
  transition: all 0.3s;
  cursor: pointer;
}

.option-card:hover {
  border-color: #1890ff;
  background-color: #f6ffed;
}

.option-card.active {
  border-color: #1890ff;
  background-color: #e6f7ff;
}

.option-content {
  margin-left: 8px;
}

.option-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.option-title {
  font-size: 16px;
  font-weight: 600;
  color: #262626;
}

.option-desc {
  font-size: 14px;
  color: #8c8c8c;
  margin-bottom: 8px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.confirmation-summary {
  margin-bottom: 24px;
}

.summary-card {
  background-color: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  padding: 16px;
}

.summary-section {
  margin-bottom: 20px;
}

.summary-section:last-child {
  margin-bottom: 0;
}

.summary-section h5 {
  margin: 0 0 12px 0;
  font-size: 14px;
  font-weight: 600;
  color: #595959;
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 8px;
}

.summary-item {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.summary-item:last-child {
  margin-bottom: 0;
}

.summary-item .label {
  color: #8c8c8c;
  min-width: 80px;
  font-size: 14px;
}

.summary-item .value {
  color: #262626;
  font-weight: 500;
  font-size: 14px;
}

.language-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.project-summary,
.execution-options {
  margin-bottom: 24px;
}

.project-summary h4,
.execution-options h4 {
  margin: 0 0 12px 0;
  font-size: 16px;
  font-weight: 600;
  color: #262626;
}

.option-icon {
  font-size: 18px;
  color: #1890ff;
}

.option-features {
  display: flex;
  gap: 8px;
}

.task-description {
  margin-bottom: 24px;
}

.task-description h4 {
  margin: 0 0 12px 0;
  font-size: 16px;
  font-weight: 600;
  color: #262626;
}

.user-selection {
  margin-bottom: 24px;
}

.user-selection h4 {
  margin: 0 0 12px 0;
  font-size: 16px;
  font-weight: 600;
  color: #262626;
}

.user-option {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
}

.user-info {
  display: flex;
  flex-direction: column;
  flex: 1;
}

.user-display-name {
  font-weight: 500;
  color: #262626;
  font-size: 14px;
}
</style>